/**
 * Timezone Configuration
 * Maps clinic/organization IDs to their respective timezones
 * This allows the frontend to dynamically display times based on clinic location
 */

// Timezone mapping by clinic ID
export const CLINIC_TIMEZONE_MAP: Record<string, string> = {
  // Add your clinic IDs here
  // Example:
  // 'singapore-clinic-id': 'Asia/Singapore',
  // 'vietnam-clinic-id': 'Asia/Ho_Chi_Minh',
};

// Timezone mapping by organization ID
export const ORGANIZATION_TIMEZONE_MAP: Record<string, string> = {
  // Add your organization IDs here
  // Example:
  // 'singapore-org-id': 'Asia/Singapore',
  // 'vietnam-org-id': 'Asia/Ho_Chi_Minh',
};

// Default timezone fallback
export const DEFAULT_TIMEZONE = "Asia/Singapore";

/**
 * Get browser's current timezone
 * @returns Browser timezone string (e.g., 'Asia/Shanghai')
 */
export const getBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return DEFAULT_TIMEZONE;
  }
};

/**
 * Check if browser timezone is valid
 * @returns true for any valid timezone
 */
export const isSupportedTimezone = (timezone: string): boolean => {
  // Accept any valid timezone string
  return Boolean(timezone && timezone.length > 0);
};

/**
 * Get timezone with fallback priority:
 * 1. Clinic ID mapping
 * 2. Organization ID mapping
 * 3. Browser timezone (if supported)
 * 4. Default timezone
 * @param clinicId - Clinic identifier
 * @param organizationId - Organization identifier
 * @returns Timezone string
 */
export const getTimezoneWithFallback = (
  clinicId: string | null,
  organizationId: string | null,
): string => {
  // Try clinic timezone first
  if (clinicId && CLINIC_TIMEZONE_MAP[clinicId]) {
    return CLINIC_TIMEZONE_MAP[clinicId];
  }

  // Try organization timezone
  if (organizationId && ORGANIZATION_TIMEZONE_MAP[organizationId]) {
    return ORGANIZATION_TIMEZONE_MAP[organizationId];
  }

  // Try browser timezone if it's in a supported region
  const browserTz = getBrowserTimezone();
  if (isSupportedTimezone(browserTz)) {
    return browserTz;
  }

  // Default fallback
  return DEFAULT_TIMEZONE;
};
